import { useCallback, useEffect, useState } from 'react';
import { HoldingAllocation, MeasurementDate } from 'api';
import { ValuationsApproach } from 'common/types/valuation';
import createEquityAllocationConfiguration from 'pages/ValuationsAllocation/allocation/EquityAllocation/config/createEquityAllocationConfiguration';
import { Configuration } from 'pages/ValuationsAllocation/hooks';
import { AllocationAttributes, RelatedEntityMeasurementDate } from 'pages/ValuationsAllocation/types';
import getCapTableSecurities from 'pages/ValuationsAllocation/util/getCapTableSecurities/getCapTableSecurities';
import { GetRiskFreeRatesHook } from 'services/hooks/allocations';
import { getCapTableVersionsByCompanyAndMD } from 'services/hooks/companies/useGetCapTableVersionsByCompanyAndMD/useGetCapTableVersionsByCompanyAndMD';
import { getValuationByAllocation } from 'services/hooks/valuations/useGetValuationByAllocation/useGetValuationByAllocation';

interface HandleRelatedEntityEquityAllocationParams {
  fieldAttributes: AllocationAttributes;
  isDisabled: boolean;
  isUniformCurrency: boolean;
  riskFreeRates: GetRiskFreeRatesHook['data'];
  approaches: ValuationsApproach[];
  currentMeasurementDate: MeasurementDate;
  childCompanies: any[];
  parentCompanyId: string;
  holdingAllocations: HoldingAllocation[];
}

const useHandleRelatedEntityEquityAllocation = ({
  fieldAttributes,
  isDisabled,
  isUniformCurrency,
  riskFreeRates,
  approaches,
  currentMeasurementDate,
  childCompanies,
  parentCompanyId,
  holdingAllocations,
}: HandleRelatedEntityEquityAllocationParams) => {
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getCapTableVersions = useCallback(
    async childCompany => {
      const currentMd = childCompany.company_measurement_dates.find(
        (md: RelatedEntityMeasurementDate) => md.date === currentMeasurementDate.date
      );

      const capTableVersions = await getCapTableVersionsByCompanyAndMD({
        companyId: childCompany.company_id,
        companyMeasurementDateId: currentMd.id,
        excludePrivateVersions: true,
      });
      return { capTableVersions };
    },
    [currentMeasurementDate]
  );

  const getAllocationForChildCompany = useCallback(
    (childCompany: any) => {
      const currentAllocation = holdingAllocations.find(alloc => alloc.company_id === childCompany.company_id);
      if (!currentAllocation){
        return;
      }
      return currentAllocation.id;
    },
    [holdingAllocations]
  );

  const fetchAndSetAllocation = useCallback(
    async (childCompany: any) => {
      const allocationId = getAllocationForChildCompany(childCompany);

      if (!allocationId) throw new Error('Allocation not found for child company');

      const valuation = await getValuationByAllocation({ allocationId });
      if (!valuation) throw new Error('Valuation not found for child company');

      const allocationScenarios = valuation.allocation?.allocation_scenarios;
      if (!allocationScenarios) throw new Error('Allocation scenarios not found for child company');

      const securities = getCapTableSecurities(allocationScenarios);

      return {
        securities,
        valuation,
      };
    },
    [getAllocationForChildCompany]
  );

  const createConfigForChildCompany = useCallback(
    async (childCompany: any) => {
      const { valuation, securities } = await fetchAndSetAllocation(childCompany);
      const { capTableVersions } = await getCapTableVersions(childCompany);

      return createEquityAllocationConfiguration({
        approaches,
        fieldAttributes,
        isDisabled,
        isUniformCurrency,
        riskFreeRates,
        capTableVersions: capTableVersions || [],
        securities,
        updateWeightingProbabilities: () => {},
        valuation,
        relatedEntity: childCompany,
        parentCompanyId,
      });
    },
    [
      parentCompanyId,
      approaches,
      fieldAttributes,
      isDisabled,
      isUniformCurrency,
      getCapTableVersions,
      fetchAndSetAllocation,
      riskFreeRates,
    ]
  );

  const configureConfigsForChildCompanies = useCallback(async () => {
    setIsLoading(true);
    const results = await Promise.all(
      childCompanies.map(async childCompany => {
        const config = await createConfigForChildCompany(childCompany);
        return {
          ...config,
          relatedEntity: childCompany,
        };
      })
    );

    setConfigurations(results);
    setIsLoading(false);
  }, [childCompanies, createConfigForChildCompany]);

  useEffect(() => {
    configureConfigsForChildCompanies();
  }, [configureConfigsForChildCompanies]);

  return { configurations, isLoading };
};

export default useHandleRelatedEntityEquityAllocation;
